<script setup lang="ts">
/**
 * The worksheet tab strip at the bottom edge of a workbook (Calc): one tab per
 * sheet, no panels — the document below is shared and the host switches what
 * it shows on `select`.
 *
 * Beyond page tabs (NxTabs) it carries what a workbook needs, each opt-in:
 * an add button right after the last tab (`addLabel`), inline rename
 * (`renamable`: double-click or F2, or `v-model:renaming-id` from a menu),
 * drag to reorder (`reorderable`; Alt+ArrowLeft/Right by keyboard), a colour
 * stripe per tab (`color`), and a right-click hook (`menu`) for the host's
 * NxContextMenu. Many tabs scroll sideways; scroll buttons appear only while
 * the tabs overflow, and the selected tab is kept in view.
 *
 * `keepFocus` stops a press on a tab from taking the focus (a formula editor
 * that picks a reference on another sheet keeps it); the click still selects.
 *
 * The bar at the bottom of the shell uses it too: a tab may carry an icon, an X
 * (`closeLabel`) and a `groupId`. Tabs of one of the `groups` stand together on
 * a quiet area behind the group's name, which folds them away. Keep a group's
 * tabs next to each other; the host owns that order.
 */
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js'; import ChevronLeft from 'lucide-vue-next/dist/esm/icons/chevron-left.js'; import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js'; import Plus from 'lucide-vue-next/dist/esm/icons/plus.js'; import X from 'lucide-vue-next/dist/esm/icons/x.js';
import {
  type Component,
  type ComponentPublicInstance,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { NxIconButton } from '../IconButton';
import NxTextInput from '../Inputs/NxTextInput.vue';

export interface NxOfficeSheetTab {
  id: string;
  label: string;
  /** CSS colour of the tab's colour stripe (a workbook tab colour); none when unset. */
  color?: string | null;
  icon?: Component;
  /** `false`: no X on this tab, even with `closeLabel`. */
  closable?: boolean;
  /** One of `groups`; none when unset. */
  groupId?: string | null;
}

export interface NxOfficeSheetTabGroup {
  id: string;
  label: string;
  collapsed?: boolean;
}

/** How an inline rename ended. */
export type NxOfficeSheetTabsRenameEnd = 'enter' | 'escape' | 'blur';

const props = withDefaults(
  defineProps<{
    tabs: readonly NxOfficeSheetTab[];
    groups?: readonly NxOfficeSheetTabGroup[];
    selectedId?: string | null;
    /** Accessible name of the tab list. */
    ariaLabel?: string;
    /** Label of the add button right after the last tab; no button without it. */
    addLabel?: string;
    addDisabled?: boolean;
    /** Lower bar and smaller tabs, for a bar that stays on screen all the time. */
    compact?: boolean;
    /** Label of the X a tab shows on hover and focus; no X without it. */
    closeLabel?: string;
    /** Tabs can be dragged to a new place (`move`). */
    reorderable?: boolean;
    /** Double-click or F2 on a tab starts the inline rename. */
    renamable?: boolean;
    /** A pointer press on a tab does not move the focus; the click still selects. */
    keepFocus?: boolean;
    /** Accessible name of the scroll buttons shown while the tabs overflow. */
    scrollBackLabel?: string;
    scrollForwardLabel?: string;
  }>(),
  {
    groups: () => [],
    selectedId: null,
    ariaLabel: undefined,
    addLabel: undefined,
    addDisabled: false,
    compact: false,
    closeLabel: undefined,
    reorderable: false,
    renamable: false,
    keepFocus: false,
    scrollBackLabel: 'Zurückblättern',
    scrollForwardLabel: 'Weiterblättern',
  }
);

/** The tab being renamed inline; the host may set it (a „Rename“ menu entry). */
const renamingId = defineModel<string | null>('renamingId', { default: null });

const emit = defineEmits<{
  /** A tab was activated (also the selected one again). */
  select: [id: string];
  add: [];
  /** `id` was dropped on `targetId` and takes its place; `null` = after the last tab. */
  move: [id: string, targetId: string | null];
  /** A tab drag ended, dropped or not. */
  'drag-end': [];
  /** Inline rename committed with a changed, non-empty name. */
  rename: [id: string, name: string];
  /** Every inline rename ends with this, changed or not. */
  'rename-end': [reason: NxOfficeSheetTabsRenameEnd];
  /** Right-click (or the context-menu key) on a tab. */
  menu: [event: MouseEvent, id: string];
  /** The X of a tab. */
  close: [id: string];
  /** The name of a group was activated: fold or unfold it. */
  'toggle-group': [groupId: string];
  /** Right-click (or the context-menu key) on the name of a group. */
  'group-menu': [event: MouseEvent, groupId: string];
  /** Right-click (or the context-menu key) on the add button. */
  'add-menu': [event: MouseEvent];
  /** `id` was dropped on the name of a group. */
  'move-to-group': [id: string, groupId: string];
}>();

const list = ref<HTMLElement | null>(null);

// ---- groups ----

const groupOf = (tab: NxOfficeSheetTab) =>
  tab.groupId ? props.groups.find((group) => group.id === tab.groupId) : undefined;

/** Neighbouring tabs of one group, and every tab without a group on its own. */
const runs = computed(() => {
  const result: { key: string; group?: NxOfficeSheetTabGroup; tabs: NxOfficeSheetTab[] }[] = [];
  for (const tab of props.tabs) {
    const group = groupOf(tab);
    const last = result[result.length - 1];
    if (group && last?.group?.id === group.id) last.tabs.push(tab);
    else result.push({ key: group ? `group:${group.id}` : `tab:${tab.id}`, group, tabs: [tab] });
  }
  return result;
});

const visibleTabs = computed(() => props.tabs.filter((tab) => !groupOf(tab)?.collapsed));
const holdsSelected = (tabs: readonly NxOfficeSheetTab[]) =>
  tabs.some((tab) => tab.id === props.selectedId);

function tabElement(id: string): HTMLElement | null {
  return (
    [...(list.value?.querySelectorAll<HTMLElement>('[data-tab-id]') ?? [])].find(
      (el) => el.dataset.tabId === id
    ) ?? null
  );
}

// ---- select + keyboard ----

function onTabMouseDown(event: MouseEvent) {
  if (props.keepFocus) event.preventDefault();
}

function onTabKeydown(event: KeyboardEvent, id: string) {
  const tabs = visibleTabs.value;
  const index = tabs.findIndex((t) => t.id === id);
  const tab = tabs[index];
  if (!tab) return;
  if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    if (!props.reorderable) return;
    event.preventDefault();
    const neighbour = tabs[index + (event.key === 'ArrowLeft' ? -1 : 1)];
    if (!neighbour) return;
    emit('move', tab.id, neighbour.id);
    void nextTick(() => tabElement(tab.id)?.focus());
    return;
  }
  if (event.key === 'F2' && props.renamable) {
    event.preventDefault();
    renamingId.value = tab.id;
    return;
  }
  const last = tabs.length - 1;
  const next = {
    ArrowLeft: index === 0 ? last : index - 1,
    ArrowRight: index === last ? 0 : index + 1,
    Home: 0,
    End: last,
  }[event.key];
  if (next === undefined || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
    return;
  event.preventDefault();
  const target = tabs[next];
  if (target) tabElement(target.id)?.focus();
}

// The roving tab stop: the selected tab, else the first.
const focusId = computed(() =>
  visibleTabs.value.some((t) => t.id === props.selectedId)
    ? props.selectedId
    : visibleTabs.value[0]?.id
);

// ---- inline rename ----

const draft = ref('');
/** The rename in progress, ended once. */
let editingId: string | null = null;
const renameField = ref<InstanceType<typeof NxTextInput> | null>(null);
const mirror = ref<HTMLElement | null>(null);
const renameWidth = ref(0);

function setRenameField(el: Element | ComponentPublicInstance | null) {
  renameField.value = el as InstanceType<typeof NxTextInput> | null;
}

watch(
  renamingId,
  async (id) => {
    if (!id) {
      editingId = null;
      return;
    }
    const tab = props.tabs.find((t) => t.id === id);
    if (!tab) {
      renamingId.value = null;
      return;
    }
    draft.value = tab.label;
    editingId = id;
    await nextTick();
    const input = renameField.value?.inputElement ?? null;
    input?.focus();
    input?.select();
  },
  { immediate: true }
);

watch([renamingId, draft], async () => {
  if (!renamingId.value) return;
  await nextTick();
  if (mirror.value) renameWidth.value = mirror.value.offsetWidth;
});

// The field is the flex item, so its size goes on the wrapper.
const renameStyle = computed(() => ({
  flexShrink: 0,
  minWidth: 'calc(var(--nxt-size-unit) * 4)',
  width: `calc(${renameWidth.value}px + var(--nxt-space-lg))`,
}));

function endRename(reason: NxOfficeSheetTabsRenameEnd) {
  // The model prop lags behind an update until the host re-renders, so the
  // blur that follows Enter / Escape must find the rename already ended.
  const id = editingId;
  if (!id) return;
  editingId = null;
  if (reason !== 'escape') {
    const name = draft.value.trim();
    const tab = props.tabs.find((t) => t.id === id);
    if (tab && name.length > 0 && name !== tab.label) emit('rename', id, name);
  }
  renamingId.value = null;
  emit('rename-end', reason);
}

function onRenameKey(event: KeyboardEvent, reason: 'enter' | 'escape') {
  if (event.isComposing) return;
  // Escape ends the rename here; the field must not restore its own way.
  event.preventDefault();
  endRename(reason);
}

function onTabDblClick(id: string) {
  if (props.renamable) renamingId.value = id;
}

// ---- drag to reorder (HTML5 drag and drop) ----

const draggedId = ref<string | null>(null);
const overId = ref<string | null>(null);
const overEnd = ref(false);
const overGroupId = ref<string | null>(null);

function resetDrag() {
  draggedId.value = null;
  overId.value = null;
  overEnd.value = false;
  overGroupId.value = null;
}

function onDragEnd() {
  resetDrag();
  emit('drag-end');
}

function acceptDrag(event: DragEvent) {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
}

function onDragStart(event: DragEvent, id: string) {
  if (!props.reorderable || renamingId.value) {
    event.preventDefault();
    return;
  }
  draggedId.value = id;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    // Firefox starts a drag only with data set.
    event.dataTransfer.setData('text/plain', id);
  }
}

function onTabDragOver(event: DragEvent, id: string) {
  if (!draggedId.value || draggedId.value === id) return;
  acceptDrag(event);
  overId.value = id;
}

function onTabDragLeave(id: string) {
  if (overId.value === id) overId.value = null;
}

function drop(targetId: string | null) {
  const id = draggedId.value;
  resetDrag();
  if (!id || !props.reorderable || id === targetId) return;
  emit('move', id, targetId);
}

function onTabDrop(event: DragEvent, id: string) {
  event.preventDefault();
  drop(id);
}

/**
 * A drop in the gap between two tabs hits the list itself, not a tab: it
 * lands on the tab whose centre is nearest, so no drop is lost to the gap.
 */
function nearestTabId(clientX: number): string | null {
  let nearest: string | null = null;
  let best = Infinity;
  for (const el of list.value?.querySelectorAll<HTMLElement>('[data-tab-id]') ?? []) {
    const rect = el.getBoundingClientRect();
    const distance = Math.abs(clientX - (rect.left + rect.width / 2));
    if (distance < best) {
      best = distance;
      nearest = el.dataset.tabId ?? null;
    }
  }
  return nearest;
}

function onListDragOver(event: DragEvent) {
  if (!draggedId.value || event.target !== event.currentTarget) return;
  acceptDrag(event);
  overId.value = nearestTabId(event.clientX);
}

function onListDrop(event: DragEvent) {
  if (event.target !== event.currentTarget) return;
  event.preventDefault();
  if (!draggedId.value) {
    resetDrag();
    return;
  }
  drop(nearestTabId(event.clientX));
}

/** The add button right after the last tab also takes a drop: „to the end“. */
function onAddDragOver(event: DragEvent) {
  if (!draggedId.value) return;
  acceptDrag(event);
  overEnd.value = true;
}

function onGroupDragOver(event: DragEvent, groupId: string) {
  if (!draggedId.value) return;
  acceptDrag(event);
  overGroupId.value = groupId;
}

function onGroupDrop(event: DragEvent, groupId: string) {
  event.preventDefault();
  const id = draggedId.value;
  resetDrag();
  if (id) emit('move-to-group', id, groupId);
}

function onAddDrop(event: DragEvent) {
  event.preventDefault();
  if (!draggedId.value) {
    resetDrag();
    return;
  }
  drop(null);
}

// ---- overflow ----

const overflowing = ref(false);
const atStart = ref(true);
const atEnd = ref(true);

function measure() {
  const el = list.value;
  if (!el) return;
  overflowing.value = el.scrollWidth > el.clientWidth + 1;
  atStart.value = el.scrollLeft <= 0;
  atEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
}

function scrollBy(direction: -1 | 1) {
  const el = list.value;
  if (!el) return;
  el.scrollBy?.({ left: direction * Math.max(el.clientWidth * 0.8, 1), behavior: 'smooth' });
}

/** Keeps a tab inside the list's visible part (only the list scrolls, never the page). */
function reveal(id: string | null | undefined) {
  const el = list.value;
  const tab = id ? tabElement(id) : null;
  if (!el || !tab) return;
  const start = tab.offsetLeft - el.offsetLeft;
  const end = start + tab.offsetWidth;
  if (start < el.scrollLeft) el.scrollLeft = start;
  else if (end > el.scrollLeft + el.clientWidth) el.scrollLeft = end - el.clientWidth;
}

let observer: ResizeObserver | null = null;
onMounted(() => {
  measure();
  reveal(props.selectedId);
  if (typeof ResizeObserver !== 'undefined' && list.value) {
    observer = new ResizeObserver(measure);
    observer.observe(list.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());

watch(
  () => [props.selectedId, props.tabs.map((t) => `${t.id}:${t.label}`).join('|')],
  async () => {
    await nextTick();
    measure();
    reveal(props.selectedId);
  }
);
</script>

<template>
  <div class="nx-office-sheet-tabs" :class="{ 'is-compact': props.compact }">
    <template v-if="overflowing">
      <NxIconButton
        :title="props.scrollBackLabel"
        :disabled="atStart"
        data-testid="nx-office-sheet-tabs-back"
        @click="scrollBy(-1)"
      >
        <ChevronLeft class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <NxIconButton
        :title="props.scrollForwardLabel"
        :disabled="atEnd"
        data-testid="nx-office-sheet-tabs-forward"
        @click="scrollBy(1)"
      >
        <ChevronRight class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
    </template>
    <div
      ref="list"
      class="nx-office-sheet-tabs__list"
      role="tablist"
      :aria-label="props.ariaLabel"
      aria-orientation="horizontal"
      @scroll.passive="measure"
      @dragover="onListDragOver"
      @drop="onListDrop"
    >
      <div
        v-for="run in runs"
        :key="run.key"
        :class="run.group ? 'nx-office-sheet-tabs__group' : 'nx-office-sheet-tabs__run'"
        :role="run.group ? 'group' : 'presentation'"
        :aria-label="run.group?.label"
      >
        <button
          v-if="run.group"
          type="button"
          class="nx-office-sheet-tabs__group-name"
          :class="{
            'is-drop-target': overGroupId === run.group.id,
            'holds-selected': run.group.collapsed && holdsSelected(run.tabs),
          }"
          :aria-expanded="run.group.collapsed ? 'false' : 'true'"
          data-testid="nx-office-sheet-tabs-group"
          @click="emit('toggle-group', run.group!.id)"
          @contextmenu.prevent="emit('group-menu', $event, run.group!.id)"
          @dragover="onGroupDragOver($event, run.group!.id)"
          @dragleave="overGroupId = null"
          @drop="onGroupDrop($event, run.group!.id)"
        >
          <component
            :is="run.group.collapsed ? ChevronRight : ChevronDown"
            class="nxt-icon-xs"
            aria-hidden="true"
          />
          {{ run.group.label }}
          <span v-if="run.group.collapsed" class="nx-office-sheet-tabs__group-count">
            {{ run.tabs.length }}
          </span>
        </button>
        <template v-if="!run.group?.collapsed">
          <template v-for="tab in run.tabs" :key="tab.id">
            <NxTextInput
              v-if="renamingId === tab.id"
              :ref="setRenameField"
              v-model="draft"
              height="xs"
              :aria-label="tab.label"
              :wrapper-style="renameStyle"
              data-testid="nx-office-sheet-tabs-rename"
              @keydown.enter="onRenameKey($event, 'enter')"
              @keydown.esc="onRenameKey($event, 'escape')"
              @blur="endRename('blur')"
            />
            <span v-else class="nx-office-sheet-tabs__item">
              <button
                type="button"
                role="tab"
                class="nx-office-sheet-tabs__tab"
                :class="{
                  'has-color': !!tab.color,
                  'has-close': !!props.closeLabel && tab.closable !== false,
                  'is-dragging': draggedId === tab.id,
                  'is-drop-target': overId === tab.id,
                }"
                :style="tab.color ? { '--tab-color': tab.color } : undefined"
                :aria-selected="tab.id === props.selectedId ? 'true' : 'false'"
                :tabindex="tab.id === focusId ? 0 : -1"
                :draggable="props.reorderable ? 'true' : 'false'"
                :data-tab-id="tab.id"
                @mousedown="onTabMouseDown"
                @click="emit('select', tab.id)"
                @dblclick="onTabDblClick(tab.id)"
                @keydown="onTabKeydown($event, tab.id)"
                @contextmenu.prevent="emit('menu', $event, tab.id)"
                @dragstart="onDragStart($event, tab.id)"
                @dragover="onTabDragOver($event, tab.id)"
                @dragleave="onTabDragLeave(tab.id)"
                @drop="onTabDrop($event, tab.id)"
                @dragend="onDragEnd"
              >
                <component
                  :is="tab.icon"
                  v-if="tab.icon"
                  class="nxt-icon-sm nx-office-sheet-tabs__icon"
                  aria-hidden="true"
                />
                <span class="nx-office-sheet-tabs__label">{{ tab.label }}</span>
              </button>
              <NxIconButton
                v-if="props.closeLabel && tab.closable !== false"
                class="nx-office-sheet-tabs__close"
                height="xs"
                variant="ghost"
                :title="props.closeLabel"
                data-testid="nx-office-sheet-tabs-close"
                @click="emit('close', tab.id)"
              >
                <X class="nxt-icon-xs" aria-hidden="true" />
              </NxIconButton>
            </span>
          </template>
        </template>
      </div>
    </div>
    <NxIconButton
      v-if="props.addLabel"
      class="nx-office-sheet-tabs__add"
      :class="{ 'is-drop-target': overEnd }"
      :title="props.addLabel"
      :disabled="props.addDisabled"
      data-testid="nx-office-sheet-tabs-add"
      @click="emit('add')"
      @contextmenu.prevent="emit('add-menu', $event)"
      @dragover="onAddDragOver"
      @dragleave="overEnd = false"
      @drop="onAddDrop"
    >
      <Plus class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
    <span
      v-if="renamingId"
      ref="mirror"
      class="nx-office-sheet-tabs__mirror"
      aria-hidden="true"
      v-text="draft || ' '"
    />
  </div>
</template>

<style scoped>
.nx-office-sheet-tabs {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  min-height: calc(var(--nxt-size-unit) * 2.25);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  box-sizing: border-box;
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-surface-bg);
}

.nx-office-sheet-tabs__list {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}

.nx-office-sheet-tabs__run {
  display: contents;
}

/* A group is a quiet area, no colour: only the selected tab carries the accent. */
.nx-office-sheet-tabs__group {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding-inline-end: calc(var(--nxt-space-2xs) / 2);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-neutral-subtle-bg);
}

.nx-office-sheet-tabs__group-name {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: calc(var(--nxt-space-2xs) / 2) var(--nxt-space-sm);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-tab-size);
  line-height: var(--nxt-line-height-normal);
  white-space: nowrap;
  cursor: pointer;
}

.nx-office-sheet-tabs__group-name:hover {
  background: var(--nxt-tab-bg-hover);
}

.nx-office-sheet-tabs__group-name:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-office-sheet-tabs__group-name.holds-selected {
  box-shadow: inset 0 calc(var(--nxt-hairline) * -2) var(--nxt-tab-indicator);
}

.nx-office-sheet-tabs__group-name.is-drop-target {
  outline: calc(var(--nxt-hairline) * 2) dashed var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-office-sheet-tabs__group-count {
  color: var(--nxt-fg-muted);
}

.nx-office-sheet-tabs__item {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  min-width: 0;
}

.nx-office-sheet-tabs__icon {
  flex: none;
}

.nx-office-sheet-tabs__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.nx-office-sheet-tabs.is-compact {
  min-height: calc(var(--nxt-size-unit) * 2.125);
  padding-block: calc(var(--nxt-space-2xs) / 2);
}

.is-compact .nx-office-sheet-tabs__tab {
  padding-inline: var(--nxt-space-sm);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
}

.is-compact .nx-office-sheet-tabs__group-name {
  font-size: var(--nxt-font-size-xs);
}

/* The X keeps its room, so a tab does not change its width on hover. */
.nx-office-sheet-tabs__tab.has-close {
  padding-inline-end: calc(var(--nxt-size-unit) * 1.5);
}

/* The X lies over the tab's reserved end, centred in it and never taller. */
.nx-office-sheet-tabs__close {
  flex: none;
  width: calc(var(--nxt-size-unit) * 1.125);
  height: calc(var(--nxt-size-unit) * 1.125);
  min-height: 0;
  margin-inline-start: calc(var(--nxt-size-unit) * -1.375);
  margin-inline-end: calc(var(--nxt-size-unit) * 0.25);
  opacity: 0;
}

.nx-office-sheet-tabs__item:hover .nx-office-sheet-tabs__close,
.nx-office-sheet-tabs__item:focus-within .nx-office-sheet-tabs__close {
  opacity: 1;
}

.nx-office-sheet-tabs__tab {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--nxt-space-2xs);
  max-width: calc(var(--nxt-size-unit) * 18);
  padding: calc(var(--nxt-space-2xs) / 2) var(--nxt-space-md);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-tab-fg);
  font: inherit;
  font-size: var(--nxt-tab-size);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-normal);
  white-space: nowrap;
  cursor: pointer;
  transition:
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-office-sheet-tabs__tab:hover {
  color: var(--nxt-tab-fg-hover);
  background: var(--nxt-tab-bg-hover);
}

/* Selection is colour only: the weight never changes, so tabs keep their place. */
.nx-office-sheet-tabs__tab[aria-selected='true'] {
  color: var(--nxt-tab-fg-selected);
  background: var(--nxt-brand-subtle-bg);
  box-shadow: inset 0 calc(var(--nxt-hairline) * -2) var(--nxt-tab-indicator);
}

/* The workbook's tab colour is a stripe on the bottom edge, selected or not. */
.nx-office-sheet-tabs__tab.has-color {
  box-shadow: inset 0 calc(var(--nxt-hairline) * -3) var(--tab-color);
}

.nx-office-sheet-tabs__tab:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-office-sheet-tabs__tab.is-dragging {
  opacity: 0.5;
}

.nx-office-sheet-tabs__tab.is-drop-target,
.nx-office-sheet-tabs__add.is-drop-target {
  background: var(--nxt-tab-bg-hover);
  outline: calc(var(--nxt-hairline) * 2) dashed var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

/* Measures the draft so the rename field grows with the name. */
.nx-office-sheet-tabs__mirror {
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  visibility: hidden;
  white-space: pre;
  font-size: var(--nxt-tab-size);
  font-weight: var(--nxt-font-weight-medium);
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .nx-office-sheet-tabs__tab {
    transition: none;
  }
}
</style>
